import React from 'react';
import { ScheduleOutlined } from '@ant-design/icons';
import './TimeSaved.css';
import CircularLoader from '../../componenets/CircularLoader';
import axios from 'axios';
import Cookies from 'js-cookie';
/**
 * `TimeSaved` is a React component that calculates and displays the time savings that the selected 
 * clinician in selected practice used the service.
 * It fetches this data from an API upon component mount or when the props change, and displays a loading
 * indicator while fetching. The component gracefully handles errors and displays the fetched time savings.
 *
 * Utilizes Ant Design icons for decorative purposes and custom CSS for styling.
 *
 * @param {Object} props - Component props.
 * @param {string} props.practice - The practice identifier for filtering the data.
 * @param {string} props.clinician - The clinician identifier for filtering the data.
 */
class TimeSaved extends React.Component {
  /**
   * Constructs the TimeSaved component with default state.
   * @param {Object} props - The props passed to the component.
   * 
   * @state {Object} state - The internal state of the component.
   * @state {number|null} state.timeSaved - The calculated time saved, initially null.
   * @state {boolean} state.isLoading - Indicates whether the data is currently being fetched.
   */
  constructor(props) {
    super(props);
    this.state = {
      timeSaved: null,
      isLoading: false,
    };
  }

  /**
   * React lifecycle method called immediately after the component is mounted.
   * It triggers the initial data fetch.
   */
  componentDidMount() {
    this.fetchData();
  }

  /**
   * Called after the component updates to fetch new data if the practice and clinician props have changed.
   * @param {Object} prevProps - Previous props for comparison.
   */
  componentDidUpdate(prevProps) {
    if (prevProps.practice !== this.props.practice || prevProps.clinician !== this.props.clinician) {
      this.fetchData();
    }
  }

  /**
   * Fetches the time savings data from the specified API endpoint. Updates the component's
   * state with the new data or sets an error state if the fetch fails.
   */
  fetchData() {
    this.setState({ isLoading: true });

    const url = `${process.env.REACT_APP_BASE_URL}users/you_saved?practice=${this.props.practice}&clinician=${this.props.clinician}`;
    
    const token = axios.defaults.headers.common.Authorization
    const config = {
      headers: {
        "Authorization": `${token}`
      }
    };

    axios.get(url, config)
      .then(response => {
        this.setState({ timeSaved: response.data.value, isLoading: false });
      })
      .catch(error => {
        console.error('Error fetching data:', error.message);
        this.setState({ isLoading: false, timeSaved: 0 });
      });

  }

  /**
   * Renders the component displaying the time savings or a loading indicator
   * while the data is being fetched.
   * 
   * @returns {React.ReactElement} The rendered component.
   */
  render() {
    const { timeSaved, isLoading } = this.state;

    return (
      <div className="container fixed-size-card">
        <ScheduleOutlined className="background-icon left-icon" />
        <div className="content">
          <div className="header">You saved</div>
          {isLoading ? (
            <CircularLoader />
          ) : (
            <div className="count">{timeSaved !== null ? timeSaved : 'Loading...'} hours</div>
          )}
          <div className="unit">this week!</div>
        </div>
        <ScheduleOutlined className="background-icon right-icon" />
      </div>
    );
  }
}

export default TimeSaved;
